import React from 'react';
import { useTranslation } from 'react-i18next';

import {
    DialogRequestTypeHeader as RequestTypeHeader,
    EmployeeName,
    ShiftCardInfo,
} from "../../../App/components/core";

import { RequestTypes, RequestStatus, ShiftSegmentTypeName } from '../../../Enums';

/**
 * @typedef {{
 *  actionType: typeof RequestTypes[keyof typeof RequestTypes];
 *  employeeFrom: *;
 *  employeeTo: *;
 *  isPartial: boolean;
 *  offerorEmployeeName: *;
 *  partialShift: { startDateTime: string; endDateTime: string; };
 *  shift: { startDateTime: string; endDateTime: string; };
 *  shifts: *;
 *  status: *;
 *  swapProposal: *;
 *  swapRequestShiftInfo: *[];
 *  swapProposalShiftInfo: *[];
 * }} Request
 */

/** @param {{ request: Request }} param0 */
const ConfirmDialogContent = ({ request = {} }) => {
    if (!Object.keys(request).length) {
        return <></>;
    }

    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'dialog' });

    const {
        actionType,
        employeeFrom,
        employeeTo,
        isPartial, // For TC & OS
        isPartialOffer, // For SS
        isPartialProposal, // For SS
        offerorEmployeeName,
        partialShift, // For TC & OS
        partialOfferShift, // For SS
        partialProposalShift, // For SS
        shift,
        status,
        swapProposal,
        swapRequestShiftInfo,
        swapProposalShiftInfo,
        startDate,
        endDate,
    } = request;

    const isApproved = status.value === RequestStatus.Approved;

    switch (actionType) {
        case RequestTypes.toCover: {
            const toCoverShiftDetails = isPartial ? partialShift : shift;
            const firstSegmentPrimaryJob = toCoverShiftDetails.segments.find(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).orgJobRef.qualifier;
            return (<>
                <RequestTypeHeader requestTypeName={'toCover'} />
                <EmployeeName
                    employeeName={employeeFrom.name}
                    employeeNumber={`(${t('employeeLabel')} 1)`}
                />
                <ShiftCardInfo
                    shiftDetails={toCoverShiftDetails}
                    employeeName={employeeTo.name}
                    employeeNumber={`(${t('employeeLabel')} 2)`}
                    shiftDescription={firstSegmentPrimaryJob}
                    actionType={actionType}
                    isApproved={isApproved}
                    isPartial={isPartial}
                />
            </>);
        }
        case RequestTypes.shiftSwap:
            let shiftSwapShiftDetails = {};
            let shiftSwapProposalShiftDetails = {};
            let offerShiftFirstSegmentPrimaryJob = '';
            let proposalShiftFirstSegmentPrimaryJob = '';
            if (swapRequestShiftInfo.length <= 1) {
                shiftSwapShiftDetails = isPartialOffer ? partialOfferShift : swapRequestShiftInfo[0].shiftInfo.shiftDetails;
                shiftSwapProposalShiftDetails = isPartialProposal ? partialProposalShift : swapProposalShiftInfo[0].shiftInfo.shiftDetails;
                offerShiftFirstSegmentPrimaryJob = shiftSwapShiftDetails.segments.find(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).orgJobRef.qualifier;
                proposalShiftFirstSegmentPrimaryJob = shiftSwapProposalShiftDetails.segments.find(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).orgJobRef.qualifier;
            } else if (swapRequestShiftInfo.length > 1) {
                shiftSwapShiftDetails = swapRequestShiftInfo.length + " Shifts";
                shiftSwapProposalShiftDetails = swapProposalShiftInfo.length + " Shifts";
            }

            return (<>
                <RequestTypeHeader requestTypeName={'shiftSwap'} />
                <ShiftCardInfo
                    shiftDetails={shiftSwapShiftDetails}
                    employeeName={offerorEmployeeName}
                    employeeNumber={`(${t('employeeLabel')} 1)`}
                    shiftDescription={offerShiftFirstSegmentPrimaryJob}
                    actionType={actionType}
                    isApproved={isApproved}
                    isPartial={isPartialOffer}
                />
                <ShiftCardInfo
                    shiftDetails={shiftSwapProposalShiftDetails}
                    employeeName={swapProposal.proposalEmployeeName}
                    employeeNumber={`(${t('employeeLabel')} 2)`}
                    shiftDescription={proposalShiftFirstSegmentPrimaryJob}
                    actionType={actionType}
                    isApproved={isApproved}
                    isPartial={isPartialProposal}
                />
            </>);
        case RequestTypes.openShift: {
            const shiftToOffer = isPartial ? partialShift : shift;
            const firstSegmentPrimaryJob = shiftToOffer.segments.find(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT).orgJobRef.qualifier;
            return (
                <>
                    <RequestTypeHeader requestTypeName={'openShift'} />
                    <ShiftCardInfo
                        shiftDetails={shiftToOffer}
                        employeeName={employeeTo.name}
                        employeeNumber={`(${t('employeeLabel')} 1)`}
                        shiftDescription={firstSegmentPrimaryJob}
                        actionType={actionType}
                        isApproved={isApproved}
                        isPartial={isPartial}
                    />
                </>
            );
        }
        case RequestTypes.voluntaryTimeOff: {
            return (<>
                <RequestTypeHeader requestTypeName={'voluntaryTimeOff'} />
                <ShiftCardInfo
                    shiftDetails={{ startDateTime: startDate, endDateTime: endDate }}
                    employeeName={employeeTo.name}
                    employeeNumber={`(${t('employeeLabel')} 1)`}
                    actionType={actionType}
                    isApproved={isApproved}
                    showDatesOnly
                />
            </>);
        }
        default:
            return <></>;
    }
};

export default ConfirmDialogContent;